import { useState, useEffect } from 'react';
import { format, addDays, startOfWeek, addWeeks, isSameDay, isBefore, startOfDay, startOfMonth, endOfMonth, eachDayOfInterval, getDay, subMonths, addMonths } from 'date-fns';
import { Calendar, Clock, Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";
import { TimePicker } from "./TimePicker";

interface ScheduleSlot {
  date: Date;
  time: string;
}

interface ScheduleCalendarProps {
  frequency: number;
  onChange?: (slots: ScheduleSlot[]) => void;
}

export function ScheduleCalendar({ frequency = 7, onChange }: ScheduleCalendarProps) {
  const [selectedSlots, setSelectedSlots] = useState<ScheduleSlot[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('12:00');

  const handleTimeChange = (newTime: string) => {
    console.log('Time changed to:', newTime);
    setSelectedTime(newTime);

    // Auto-add slot when both date and time are selected (for simple single scheduling)
    if (selectedDate && newTime && selectedSlots.length === 0) {
      const newSlot = { date: selectedDate, time: newTime };
      const updatedSlots = [newSlot];
      console.log('Auto-adding slot:', newSlot);
      setSelectedSlots(updatedSlots);
      onChange?.(updatedSlots);
    }
  };

  // Generate calendar days for the current month
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = addDays(calendarStart, 41); // 6 weeks = 42 days

  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const handleAddSlot = () => {
    if (selectedDate && selectedTime && selectedSlots.length < frequency) {
      console.log('Adding slot with time:', selectedTime);
      const newSlot = { date: selectedDate, time: selectedTime };
      const updatedSlots = [...selectedSlots, newSlot].sort((a, b) => a.date.getTime() - b.date.getTime());
      console.log('Updated slots:', updatedSlots);
      setSelectedSlots(updatedSlots);
      onChange?.(updatedSlots);
      setSelectedDate(null);
    }
  };

  const handleRemoveSlot = (index: number) => {
    const updatedSlots = selectedSlots.filter((_, i) => i !== index);
    setSelectedSlots(updatedSlots);
    onChange?.(updatedSlots);
  };

  const handleDateClick = (date: Date) => {
    // Don't allow selecting past dates
    if (isBefore(startOfDay(date), startOfDay(new Date()))) {
      return;
    }
    const newSelectedDate = selectedDate && isSameDay(date, selectedDate) ? null : date;
    setSelectedDate(newSelectedDate);

    // Auto-add slot when both date and time are selected (for simple single scheduling)
    if (newSelectedDate && selectedTime && selectedSlots.length === 0) {
      const newSlot = { date: newSelectedDate, time: selectedTime };
      const updatedSlots = [newSlot];
      console.log('Auto-adding slot after date selection:', newSlot);
      setSelectedSlots(updatedSlots);
      onChange?.(updatedSlots);
    }
  };

  return (
    <div className="space-y-4">
      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium">Select Days & Times</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[140px] text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="h-8 w-8"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="space-y-2">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
            <div key={day} className="h-8 flex items-center justify-center">
              <span className="text-xs font-medium text-muted-foreground">{day}</span>
            </div>
          ))}
        </div>

        {/* Calendar dates */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((date) => {
            const isPastDate = isBefore(startOfDay(date), startOfDay(new Date()));
            const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
            const isToday = isSameDay(date, new Date());

            return (
              <Button
                key={date.toString()}
                variant="ghost"
                disabled={isPastDate}
                className={cn(
                  "h-8 w-full text-sm relative p-0",
                  !isCurrentMonth && "text-muted-foreground/40",
                  selectedDate && isSameDay(date, selectedDate) && "bg-primary text-primary-foreground",
                  selectedSlots.some(slot => isSameDay(slot.date, date)) && "border-2 border-primary",
                  isPastDate && "opacity-50 cursor-not-allowed",
                  isToday && "font-bold text-primary",
                  isCurrentMonth && !isPastDate && "hover:bg-secondary"
                )}
                onClick={() => handleDateClick(date)}
              >
                {format(date, 'd')}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Time Selection */}
      {selectedDate && (
        <div className="mt-4 space-y-2">
          <Label>Release Time</Label>
          <div className="flex items-center gap-2">
            <TimePicker
              value={selectedTime}
              onChange={handleTimeChange}
              className="flex-1"
            />
            <Button
              onClick={handleAddSlot}
              disabled={selectedSlots.length >= frequency}
              className="h-9"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Slot
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            Selected: {format(selectedDate, 'MMM d, yyyy')} at {selectedTime}
          </div>
        </div>
      )}

      {/* Selected Slots */}
      {selectedSlots.length > 0 && (
        <div className="space-y-2 mt-4">
          <Label>Selected Release Slots ({selectedSlots.length}/{frequency})</Label>
          <div className="space-y-2">
            {selectedSlots.map((slot, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 rounded-md bg-secondary/50"
              >
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {format(slot.date, 'MMM d, yyyy')} at {slot.time}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveSlot(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}